/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AccountFilterModel,
  PermissionValues,
  useAccountFilter,
  useCurrentAccount,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useParams } from 'react-router-dom';
import { AccountList } from './accountList';

export type SelectedAccountRow = {
  id: string;
  groups?: Array<{ id: string; name: string }>;
};

type AccountsComponentProps = {
  onRowsSelectedChange?: (rows: SelectedAccountRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: 'idle' | 'reset' | 'update-pending';
  onSelectionStatusChange?: (status: 'idle' | 'reset' | 'update-pending') => void;
};

export const AccountsComponent = ({
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus,
  onSelectionStatusChange,
}: AccountsComponentProps) => {
  const { workspaceId } = useParams();
  const { data: accountFilter, isLoading: isLoadingFilter } = useAccountFilter();
  const { account } = useCurrentAccount();

  const workspace = useCurrentWorkspace(true);

  const showCheckboxColumn = useMemo(
    () => !!workspace,
    [workspace]
  );

  const isGlobalAdmin = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const currentWorkspaceId = useCallback(() => {
    if (workspaceId) {
      return workspaceId;
    }

    if (workspace) {
      return workspace.id;
    }

    return undefined;
  }, [workspace, workspaceId]);

  const filter = useMemo(() => {
    if (!accountFilter || workspace === undefined) {
      return null;
    }

    const filter: AccountFilterModel = new AccountFilterModel(accountFilter);
    filter.workspaceId = currentWorkspaceId();

    return filter.getGraphQLFilter();
  }, [accountFilter, workspace, currentWorkspaceId]);

  const updateRowsSelected = useCallback(
    (rows: Array<any>) => {
      onRowsSelectedChange?.(
        rows.map((row) => ({
          id: row.id,
          groups: row.groups,
        }))
      );
    },
    [onRowsSelectedChange]
  );

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  if (isLoadingFilter) {
    return null;
  }

  return !filter ? null : (
    <View style={styles.container}>
      <AccountList
        filter={filter}
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        showCheckboxColumn={showCheckboxColumn}
        showRolesColumn={!!currentWorkspaceId()}
        showImpersonateWorkspace={isGlobalAdmin}
        showImpersonateUser={isGlobalAdmin}
        showGlobalAdminRole={isGlobalAdmin}
        workspaceId={currentWorkspaceId()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
});
